import { useEffect, useState, useRef, useReducer } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Store, ArrowDown, TrendingDown, Bell, BellRing } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// --- Price-watch (Drop Alerts) shared store: load the shopper's followed product ids once,
// then let every FeedCard toggle instantly without N requests. Guests get an empty set. ---
let _watched = null;
const _watchListeners = new Set();
async function _loadWatches() {
  try { const r = await api.get("/me/price-watches"); _watched = new Set((r.data.watches || []).map((w) => w.product_id)); }
  catch { _watched = new Set(); }
  _watchListeners.forEach((l) => l());
}
export function refreshWatches() { return _loadWatches(); }
export function usePriceWatch(productId) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const l = () => force();
    _watchListeners.add(l);
    if (_watched === null) { _watched = new Set(); _loadWatches(); }
    return () => { _watchListeners.delete(l); };
  }, []);
  const following = !!(_watched && _watched.has(productId));
  const toggle = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    try {
      if (following) {
        await api.delete(`/me/price-watches/${productId}`);
        _watched.delete(productId); toast.success("Stopped following — no more price alerts");
      } else {
        await api.post("/me/price-watches", { product_id: productId });
        _watched.add(productId); toast.success("Following — we'll ping you if the price drops");
      }
      _watchListeners.forEach((l) => l());
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Log in to follow items for price drops" : "Could not update");
    }
  };
  return { following, toggle };
}

const outHref = (u, m = "") => `/out?u=${encodeURIComponent(u || "")}&src=cuelinks${m ? `&m=${encodeURIComponent(m)}` : ""}`;
const SYM = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED " };

const timeAgo = (iso) => {
  if (!iso) return null;
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

// Real Cuelinks product cards (image + price) from approved merchant feeds. Shown ABOVE the
// coupon/deal rail on Shop. Silent no-op when no feeds are configured or nothing matches.
// Never renders a fabricated price — price is shown only when the feed supplied one.
export const FeedProducts = ({ q = "", limit = 8, onMerchants }) => {
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [logos, setLogos] = useState({});
  const [active, setActive] = useState("");        // selected merchant filter ("" = all)
  const [syncedAt, setSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soft, setSoft] = useState(false);
  useEffect(() => {
    let live = true; setLoading(true);
    const p = new URLSearchParams({ limit: String(limit) });
    if (q) p.set("q", q);
    if (active) p.set("merchant", active);
    api.get(`/marketplace/public/products-feed?${p.toString()}`)
      .then((r) => {
        if (!live) return;
        const list = r.data?.enabled ? (r.data.products || []) : [];
        setItems(list);
        setStores(r.data?.enabled ? (r.data.stores || []) : []);
        setSoft(!!r.data?.soft_fallback);
        const ms = r.data?.merchants || [];
        setMerchants(ms);
        setLogos(r.data?.merchant_logos || {});
        setSyncedAt(r.data?.synced_at || null);
        onMerchants?.(ms);
      })
      .catch(() => { if (live) { setItems([]); setStores([]); onMerchants?.([]); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [q, limit, active]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reset filters whenever the search query changes.
  useEffect(() => { setActive(""); }, [q]);
  if (!loading && items.length === 0 && stores.length === 0 && !active) return null;
  const fresh = timeAgo(syncedAt);
  const fallback = !loading && items.length === 0 && stores.length > 0;
  return (
    <div className="mb-6" data-testid="feed-products">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-700">{fallback ? "Partner stores for your search" : "Products from our partners"}</h3>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Affiliate</span>
        {fresh && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 ring-1 ring-emerald-100" data-testid="feed-freshness">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Synced {fresh}
          </span>
        )}
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-slate-400">Best value first</span>
      </div>
      <p className="mb-3 text-[11px] text-slate-400" data-testid="partner-price-note">Partner prices &amp; stock are indicative — final price and availability are confirmed on the store's site.</p>
      {merchants.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="feed-merchant-filter">
          <button type="button" onClick={() => setActive("")} data-testid="feed-merchant-all"
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize transition ${active === "" ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
            All stores
          </button>
          {merchants.map((m) => (
            <button key={m} type="button" onClick={() => setActive(m)} data-testid={`feed-merchant-${m}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold capitalize transition ${active === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
              {logos[m] && <img src={logos[m]} alt="" className="h-3.5 w-3.5 rounded-sm" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              {m}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-white/70" />)}</div>
      ) : (() => {
        const productsBlock = items.length === 0 ? (
          <p className="rounded-xl bg-white/70 px-4 py-6 text-center text-sm text-slate-400" data-testid="feed-merchant-empty">{active ? "No products from this store right now." : "No exact product match — here are partner stores & deals for your search."}</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((p) => <FeedCard key={p.id} p={p} />)}
          </div>
        );
        const storesBlock = stores.length > 0 ? (
          <div className={soft ? "" : "mt-6"} data-testid="feed-stores">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{items.length === 0 ? "Partner deals for your search" : soft ? "Top partner stores for your search" : "More partner stores"}</p>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {stores.map((s) => <StoreCard key={s.id} s={s} />)}
            </div>
          </div>
        ) : null;
        return soft
          ? <>{storesBlock}<div className="mt-6">{productsBlock}</div></>
          : <>{productsBlock}{storesBlock}</>;
      })()}
    </div>
  );
};

const StoreCard = ({ s }) => {
  const pv = s.payout != null ? Math.round(Number(s.payout) * 100) / 100 : null;
  const isPct = s.payout_model === "percentage" || String(s.payout_model || "").includes("percent");
  const dealText = s.deal_text || (pv != null ? (isPct ? `Up to ${pv}% back` : `₹${pv} back`) : null);
  const sid = s.spotlight ? String(s.id).replace(/^spot-/, "") : null;
  const track = () => { if (sid) { try { api.post(`/marketplace/public/spotlight-click/${sid}`); } catch { /* ignore */ } } };
  return (
    <a href={outHref(s.url, s.merchant)} onClick={track} target="_blank" rel="nofollow sponsored noopener noreferrer" data-testid={`feed-store-${s.id}`}
      className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-center gap-2">
        {s.logo
          ? <img src={s.logo} alt="" className="h-8 w-8 flex-shrink-0 rounded-lg object-contain ring-1 ring-slate-100" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50 text-sm font-bold text-indigo-500 ring-1 ring-slate-200">{(s.merchant || "?").charAt(0).toUpperCase()}</span>}
        <span className="truncate text-sm font-semibold capitalize text-slate-800">{s.merchant}</span>
        {s.spotlight && <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700" data-testid={`feed-store-featured-${s.id}`}>★ Featured</span>}
      </div>
      <div className="flex items-center justify-between gap-1">
        {dealText
          ? <span className="truncate rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600 ring-1 ring-emerald-100" data-testid={`feed-store-deal-${s.id}`}>{dealText}</span>
          : <span />}
        <span className="shrink-0 text-[10px] font-semibold text-slate-400 opacity-0 transition group-hover:opacity-100">Shop →</span>
      </div>
    </a>
  );
};

const FeedCard = ({ p }) => {
  const [imgOk, setImgOk] = useState(true);
  const { following, toggle } = usePriceWatch(p.id);
  const sym = SYM[p.currency] || `${p.currency} `;
  const pv = p.payout != null ? Math.round(Number(p.payout) * 100) / 100 : null;
  const payoutLabel = pv != null
    ? (p.payout_model === "percentage" || String(p.payout_model || "").includes("percent")
        ? `Up to ${pv}% back` : `${sym}${pv} back`)
    : null;
  const off = p.discount != null ? p.discount
    : (p.mrp && p.price && p.mrp > p.price ? Math.round((p.mrp - p.price) / p.mrp * 100) : null);
  return (
    <a href={outHref(p.url, p.merchant)} target="_blank" rel="nofollow sponsored noopener noreferrer" data-testid={`feed-product-${p.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className="relative flex h-40 items-center justify-center overflow-hidden border-b border-slate-100 bg-white p-3">
        {off ? <span className="absolute left-2 top-2 z-10 rounded-md bg-rose-600 px-1.5 py-0.5 text-[10px] font-extrabold text-white shadow-sm" data-testid={`feed-discount-${p.id}`}>{off}% OFF</span> : null}
        {p.price_dropped ? <span className="absolute right-2 top-2 z-10 inline-flex items-center gap-0.5 rounded-md bg-emerald-600 px-1.5 py-0.5 text-[10px] font-extrabold text-white shadow-sm" data-testid={`feed-pricedrop-${p.id}`}><ArrowDown className="h-2.5 w-2.5" />PRICE DROP</span> : null}
        <button type="button" onClick={toggle} title={following ? "Following — you'll get price-drop alerts" : "Follow for price-drop alerts"} data-testid={`feed-follow-${p.id}`}
          className={`absolute bottom-2 right-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full shadow-sm ring-1 transition ${following ? "bg-amber-500 text-white ring-amber-500" : "bg-white/90 text-slate-500 ring-slate-200 hover:text-amber-600"}`}>
          {following ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
        </button>
        {p.image && imgOk ? <img src={p.image} alt={p.title} loading="lazy" className="max-h-full max-w-full object-contain" onError={() => setImgOk(false)} />
          : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 text-2xl font-bold text-indigo-500 ring-1 ring-slate-200">{(p.merchant || "?").charAt(0).toUpperCase()}</div>}
      </div>
      <div className="flex flex-1 flex-col p-3">
        {p.merchant && <p className="mb-0.5 flex items-center gap-1 truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Store className="h-3 w-3" />{p.merchant}</p>}
        <p className="line-clamp-2 text-sm font-medium text-slate-800">{p.title}</p>
        {payoutLabel && <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600 ring-1 ring-emerald-100" data-testid={`feed-payout-${p.id}`}>{payoutLabel}</span>}
        <div className="mt-auto flex items-baseline gap-1.5 pt-2">
          {p.price != null ? (<><span className="font-bold text-slate-900" data-testid={`feed-price-${p.id}`}>{sym}{p.price}</span>{p.mrp && p.mrp > p.price && <span className="text-xs text-slate-400 line-through">{sym}{p.mrp}</span>}</>) : (
            <span className="text-xs font-semibold text-slate-500">See price on site</span>
          )}
        </div>
        <span className="mt-2 inline-flex items-center justify-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition group-hover:bg-slate-700">View deal <ExternalLink className="h-3 w-3" /></span>
      </div>
    </a>
  );
};

export const TopDeals = ({ limit = 10 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    api.get(`/marketplace/public/top-deals?limit=${limit}`)
      .then((r) => { if (live) setItems(r.data?.enabled ? (r.data.products || []) : []); })
      .catch(() => { if (live) setItems([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [limit]);
  if (!loading && items.length === 0) return null;
  return (
    <div className="mb-6" data-testid="top-deals">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-base font-semibold text-slate-700">Top deals today</h3>
        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-600 ring-1 ring-rose-100">Biggest discounts</span>
      </div>
      {loading ? (
        <div className="flex gap-4 overflow-hidden">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-64 w-44 flex-shrink-0 animate-pulse rounded-2xl bg-white/70" />)}</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {items.map((p) => <div key={p.id} className="w-44 flex-shrink-0"><FeedCard p={p} /></div>)}
        </div>
      )}
    </div>
  );
};

export default FeedProducts;

// Profile → the shopper's followed items with live price vs the price when they started following.
export const WatchedPrices = () => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/me/price-watches").then((r) => setRows(r.data.watches || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const unfollow = async (id) => {
    try { await api.delete(`/me/price-watches/${id}`); setRows((rs) => rs.filter((w) => w.product_id !== id)); refreshWatches(); toast.success("Stopped following"); }
    catch { toast.error("Could not update"); }
  };
  if (rows === null) return <div className="h-16 animate-pulse rounded-xl bg-slate-100" />;
  if (rows.length === 0) return <p className="text-sm text-slate-400" data-testid="watched-empty">You're not following any items yet. Tap the bell on a product to get pinged when its price drops.</p>;
  return (
    <div className="space-y-2" data-testid="watched-prices">
      {rows.map((w) => {
        const sym = SYM[w.currency] || "";
        return (
          <div key={w.product_id} data-testid={`watched-row-${w.product_id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5">
            {w.image ? <img src={w.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-contain ring-1 ring-slate-100" /> : <div className="h-12 w-12 shrink-0 rounded-lg bg-slate-50" />}
            <div className="min-w-0 flex-1">
              <a href={outHref(w.url, w.merchant)} target="_blank" rel="nofollow sponsored noopener noreferrer" className="line-clamp-1 text-sm font-medium text-slate-800 hover:text-sky-600">{w.title}</a>
              <p className="mt-0.5 flex items-center gap-2 text-xs">
                {w.current_price != null && <span className="font-bold text-slate-900">{sym}{w.current_price}</span>}
                {w.dropped ? <span className="inline-flex items-center gap-0.5 font-semibold text-emerald-600"><ArrowDown className="h-3 w-3" /> from {sym}{w.followed_price}</span>
                  : w.followed_price != null ? <span className="text-slate-400">followed at {sym}{w.followed_price}</span> : null}
                {!w.available && <span className="text-rose-500">out of stock</span>}
              </p>
            </div>
            <button onClick={() => unfollow(w.product_id)} data-testid={`watched-unfollow-${w.product_id}`} className="shrink-0 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50">Unfollow</button>
          </div>
        );
      })}
    </div>
  );
};

const Reveal = ({ children, delay = 0, className = "", testid }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} data-testid={testid} className={className}
      style={{ transition: "opacity .5s ease, transform .5s ease", transitionDelay: `${delay}ms`, opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(16px)" }}>
      {children}
    </div>
  );
};

export const PickedForYou = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    api.get("/marketplace/search/history")
      .then((r) => {
        const ts = (r.data?.history || []).map((h) => h.q).filter(Boolean).slice(0, 5);
        if (!live) return null;
        if (ts.length === 0) { setLoading(false); return null; }
        return api.get(`/marketplace/public/picked-for-you?q=${encodeURIComponent(ts.join(","))}&limit=10`)
          .then((rr) => { if (live) setItems(rr.data?.enabled ? (rr.data.products || []) : []); });
      })
      .catch(() => { if (live) setItems([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  if (loading || items.length === 0) return null;
  return (
    <Reveal className="mb-6" testid="picked-for-you">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-base font-semibold text-slate-700">Picked for you</h3>
        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-600 ring-1 ring-indigo-100">Based on your searches</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {items.map((p) => <div key={p.id} className="w-44 flex-shrink-0"><FeedCard p={p} /></div>)}
      </div>
    </Reveal>
  );
};

// One admin-featured merchant feed as a bold Shop-home spotlight rail (biggest discounts first).
export const FeedSpotlight = () => {
  const [data, setData] = useState({ enabled: false, products: [] });
  useEffect(() => {
    let live = true;
    api.get("/marketplace/public/products-spotlight?limit=10")
      .then((r) => { if (live) setData(r.data?.enabled ? r.data : { enabled: false, products: [] }); })
      .catch(() => { if (live) setData({ enabled: false, products: [] }); });
    return () => { live = false; };
  }, []);
  if (!data.enabled || data.products.length === 0) return null;
  return (
    <div className="mb-6 overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600 p-5 text-white shadow-lg" data-testid="feed-spotlight">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">Featured store</span>
        <h3 className="text-xl font-extrabold capitalize tracking-tight sm:text-2xl" data-testid="feed-spotlight-merchant">{data.merchant}</h3>
        <span className="text-xs text-white/70">{data.products.length} handpicked deals</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-1">
        {data.products.map((p) => <div key={p.id} className="w-40 flex-shrink-0"><FeedCard p={p} /></div>)}
      </div>
    </div>
  );
};

// Items whose price dropped in the latest feed sync — a freshness rail so shoppers catch new deals.
export const JustDropped = () => {
  const [data, setData] = useState({ enabled: false, products: [] });
  useEffect(() => {
    let live = true;
    api.get("/marketplace/public/just-dropped?limit=12")
      .then((r) => { if (live) setData(r.data?.enabled ? r.data : { enabled: false, products: [] }); })
      .catch(() => { if (live) setData({ enabled: false, products: [] }); });
    return () => { live = false; };
  }, []);
  if (!data.enabled || data.products.length === 0) return null;
  return (
    <div className="mb-6" data-testid="just-dropped">
      <div className="mb-3 flex items-center gap-2">
        <TrendingDown className="h-5 w-5 text-rose-500" />
        <h3 className="text-lg font-bold text-slate-900">Just dropped</h3>
        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-600">Fresh price cuts</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-1">
        {data.products.map((p) => (
          <div key={p.id} className="relative w-40 flex-shrink-0" data-testid={`just-dropped-item-${p.id}`}>
            {p.drop_pct > 0 && <span className="absolute left-1.5 top-1.5 z-10 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white shadow" data-testid={`just-dropped-badge-${p.id}`}>↓ {p.drop_pct}%</span>}
            <FeedCard p={p} />
          </div>
        ))}
      </div>
    </div>
  );
};

// One storefront strip per admin-uploaded brand, with quick-jump chips. Silent when nothing is
// uploaded / vCommission is off. White-label: shows the admin's brand label, not any network name.
export const BrandRails = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("");
  useEffect(() => {
    let live = true;
    api.get("/marketplace/public/brand-rails?limit_per=8")
      .then((r) => { if (live) setBrands(r.data?.enabled ? (r.data.brands || []) : []); })
      .catch(() => { if (live) setBrands([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  if (loading || brands.length === 0) return null;
  const spot = brands.find((b) => b.spotlight);
  const shown = active ? brands.filter((b) => b.key === active) : brands;
  return (
    <div className="mb-6" data-testid="brand-rails">
      {spot && !active && (
        <div className="mb-6 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-900 to-fuchsia-800 p-5 text-white shadow-lg" data-testid={`brand-spotlight-${spot.key}`}>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">Featured brand</span>
            <h3 className="text-xl font-extrabold capitalize tracking-tight sm:text-2xl">{spot.label}</h3>
            <span className="text-xs text-white/60">{spot.count} products</span>
            <button onClick={() => setActive(spot.key)} data-testid={`brand-spotlight-shop-${spot.key}`} className="ml-auto rounded-full bg-white px-4 py-1.5 text-xs font-bold text-slate-900 transition hover:bg-white/90">Shop the collection →</button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-1">
            {spot.products.slice(0, 8).map((p) => <div key={p.id} className="w-40 flex-shrink-0"><FeedCard p={p} /></div>)}
          </div>
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-700">Shop by brand</h3>
        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-600 ring-1 ring-violet-100">Curated</span>
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="brand-rail-chips">
        <button type="button" onClick={() => setActive("")} data-testid="brand-chip-all"
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${active === "" ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>All brands</button>
        {brands.map((b) => (
          <button key={b.key} type="button" onClick={() => setActive(b.key === active ? "" : b.key)} data-testid={`brand-chip-${b.key}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize transition ${active === b.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
            {b.label} <span className="opacity-60">({b.count})</span>
          </button>
        ))}
      </div>
      {shown.map((b, i) => (
        <Reveal key={b.key} delay={(i % 4) * 90} className="mb-6" testid={`brand-row-${b.key}`}>
          <div className="mb-2 flex items-center gap-2">
            <h4 className="text-sm font-semibold capitalize text-slate-700">{b.label}</h4>
            <span className="text-xs text-slate-400">{b.count} products</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {b.products.map((p) => <div key={p.id} className="w-44 flex-shrink-0"><FeedCard p={p} /></div>)}
          </div>
        </Reveal>
      ))}
    </div>
  );
};

export const CategoryRows = () => {
  const [cats, setCats] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    api.get("/marketplace/public/product-categories?limit_per=6")
      .then((r) => { if (!live) return; setCats(r.data?.enabled ? (r.data.categories || []) : []); setStores(r.data?.stores || []); })
      .catch(() => { if (live) { setCats([]); setStores([]); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  if (!loading && cats.length === 0 && stores.length === 0) return null;
  return (
    <div className="mb-6" data-testid="category-rows">
      {cats.map((c, i) => (
        <Reveal key={c.key} delay={(i % 4) * 90} className="mb-6" testid={`category-row-${c.key}`}>
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-base font-semibold capitalize text-slate-700">{c.label}</h3>
            <span className="text-xs text-slate-400">{c.count}</span>
            {c.avg_discount > 0 && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600 ring-1 ring-rose-100">avg {c.avg_discount}% off</span>}
            <Link to={`/shop/search/${encodeURIComponent(c.label)}`} data-testid={`category-seeall-${c.key}`} className="ml-auto text-xs font-semibold text-indigo-600 hover:underline">See all →</Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {c.products.map((p) => <div key={p.id} className="w-44 flex-shrink-0"><FeedCard p={p} /></div>)}
          </div>
        </Reveal>
      ))}
      {stores.length > 0 && (
        <div className="mt-2" data-testid="feed-stores">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">More partner stores</p>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {stores.map((s) => <StoreCard key={s.id} s={s} />)}
          </div>
        </div>
      )}
    </div>
  );
};